import type { LotOrigin } from '@gsls/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, ErrorNote, inputClass } from '../../../components/ui';
import { ApiError, api, type PendingLot } from '../../../lib/api';
import { formatQuantity, humanise } from '../../../lib/format';
import { links } from '../../../lib/links';
import { daysSince } from '../../../lib/periods';
import { onHoldQuery } from '../queries';
import {
  RowTitle,
  WidgetCard,
  WidgetEmpty,
  WidgetError,
  WidgetList,
  WidgetSkeleton,
} from '../primitives';

/**
 * The quality hold queue, oldest first — the age is the point, because material
 * sitting on hold is stock nobody can use.
 *
 * When the viewer can release material, the decision is made here: a confirm
 * step keeps a one-way rejection deliberate, but there is no reason to send a
 * reviewer to another screen to clear a queue.
 */
export function HoldQueueWidget({
  limit = 5,
  canDecide = false,
  origin,
  title = 'Waiting for release',
  to,
}: {
  limit?: number;
  canDecide?: boolean;
  origin?: LotOrigin;
  title?: string;
  to?: string;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<{
    lot: PendingLot;
    outcome: 'passed' | 'failed';
  } | null>(null);
  const [result, setResult] = useState('');

  const query = useQuery(onHoldQuery);

  const decide = useMutation({
    mutationFn: (variables: { lotId: string; outcome: 'passed' | 'failed'; result?: string }) =>
      api.quality.decide({
        lotId: variables.lotId,
        outcome: variables.outcome,
        ...(variables.result ? { result: variables.result } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['quality'] });
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setConfirming(null);
      setResult('');
    },
  });

  const all = query.data?.items ?? [];
  const matching = origin ? all.filter((lot) => lot.origin === origin) : all;
  const rows = matching.slice(0, limit);
  const total = matching.length;

  return (
    <WidgetCard
      title={title}
      description="Material that is counted and valued, but cannot be used yet."
      to={to}
      toLabel="Full queue"
      badge={total > 0 ? <Badge tone="hazard">{total}</Badge> : null}
    >
      {query.isPending ? <WidgetSkeleton rows={2} /> : null}
      {query.isError ? (
        <WidgetError label="Could not load the hold queue." onRetry={() => void query.refetch()} />
      ) : null}
      {query.isSuccess && rows.length === 0 ? (
        <WidgetEmpty>Nothing is on hold.</WidgetEmpty>
      ) : null}

      {rows.length > 0 ? (
        <WidgetList>
          {rows.map((lot) => {
            const age = daysSince(lot.onHoldSince);
            return (
              <li key={lot.lotId} className="flex items-start justify-between gap-3 py-3">
                <RowTitle
                  title={lot.lotCode}
                  subtitle={
                    <>
                      <span className="font-mono">{lot.itemCode}</span> · {lot.itemName}
                    </>
                  }
                />
                <div className="flex shrink-0 flex-col items-end gap-1 text-right">
                  <p className="whitespace-nowrap text-sm font-semibold text-ink">
                    {formatQuantity(lot.onHand, lot.baseUom)}
                  </p>
                  <div className="flex items-center gap-1.5">
                    <Badge tone={lot.origin === 'recovered' ? 'accent' : 'muted'}>
                      {humanise(lot.origin)}
                    </Badge>
                    <Badge tone={age >= 7 ? 'hazard' : 'muted'}>
                      {age === 0 ? 'new' : `${age}d`}
                    </Badge>
                  </div>
                  {canDecide ? (
                    <div className="mt-1 flex gap-2">
                      <Button
                        tone="ghost"
                        onClick={() => {
                          setConfirming({ lot, outcome: 'passed' });
                          setResult('');
                          decide.reset();
                        }}
                      >
                        Release
                      </Button>
                      <Button
                        tone="danger"
                        onClick={() => {
                          setConfirming({ lot, outcome: 'failed' });
                          setResult('');
                          decide.reset();
                        }}
                      >
                        Reject
                      </Button>
                    </div>
                  ) : (
                    <Link
                      to={links.qualityLot(lot.lotId)}
                      className="mt-1 text-xs font-bold uppercase tracking-wide text-brand-deep hover:underline"
                    >
                      Decide
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </WidgetList>
      ) : null}

      {confirming ? (
        <div
          className={`mt-3 rounded-md border-l-4 p-4 ${
            confirming.outcome === 'failed'
              ? 'border-l-red-400 bg-red-50'
              : 'border-l-accent bg-accent-soft'
          }`}
        >
          <h3 className="text-sm font-bold uppercase tracking-wide text-ink">
            {confirming.outcome === 'failed' ? 'Reject' : 'Release'} {confirming.lot.lotCode}
          </h3>
          <p className="mt-1 text-sm text-ink-body">
            {confirming.outcome === 'failed'
              ? 'Rejecting is a one-way door — the lot has to be reopened deliberately before it can be tested again.'
              : 'Releasing makes the whole lot usable in production immediately.'}{' '}
            {formatQuantity(confirming.lot.onHand, confirming.lot.baseUom)} ·{' '}
            {humanise(confirming.lot.origin)}
            {confirming.lot.supplierName ? ` · ${confirming.lot.supplierName}` : ''}
          </p>

          <div className="mt-3">
            <input
              className={inputClass}
              value={result}
              onChange={(event) => setResult(event.target.value)}
              placeholder="Result (optional) — e.g. GC 99.6%"
              aria-label="Quality result"
            />
          </div>

          {decide.error ? (
            <div className="mt-3">
              <ErrorNote>
                {decide.error instanceof ApiError
                  ? decide.error.message
                  : 'Could not record the decision.'}
              </ErrorNote>
            </div>
          ) : null}

          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              {...(confirming.outcome === 'failed' ? { tone: 'danger' as const } : {})}
              disabled={decide.isPending}
              onClick={() =>
                decide.mutate({
                  lotId: confirming.lot.lotId,
                  outcome: confirming.outcome,
                  ...(result.trim() ? { result: result.trim() } : {}),
                })
              }
            >
              {decide.isPending
                ? 'Saving…'
                : confirming.outcome === 'failed'
                  ? 'Reject permanently'
                  : 'Release — make it usable'}
            </Button>
            <Button tone="ghost" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </WidgetCard>
  );
}
